import React from 'react';
import { Calendar, Users, Phone, Wallet, FileText, Bell, Pin } from 'lucide-react';
import { CalendarEvent, CalendarEventType } from '../services/calendarService';

/**
 * Hebrew month names
 */
export const getHebrewMonthName = (month: number): string => {
  const months = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
  ];
  return months[month];
};

/**
 * Hebrew day names
 */
export const getHebrewDayName = (day: number): string => {
  const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  return days[day];
};

/**
 * Short Hebrew day names (for calendar header)
 */
export const getShortHebrewDayName = (day: number): string => {
  const days = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
  return days[day];
};

/**
 * Format event time (HH:mm)
 */
export const formatEventTime = (dateTime: string): string => {
  const date = new Date(dateTime);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Format event date (DD/MM/YYYY)
 */
export const formatEventDate = (dateTime: string): string => {
  const date = new Date(dateTime);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Format event date and time (DD/MM/YYYY HH:mm)
 */
export const formatEventDateTime = (dateTime: string): string => {
  return `${formatEventDate(dateTime)} ${formatEventTime(dateTime)}`;
};

/**
 * Check if date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

/**
 * Check if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear();
};

/**
 * Get days in month
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Get first day of month (0 = Sunday, 6 = Saturday)
 */
export const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

/**
 * Priority logic for displaying events in calendar cells:
 * - 1 meeting/appointment
 * - 1 committee
 * - 1 high-priority reminder
 * Maximum 3 events per day cell
 */
export const prioritizeEvents = (events: CalendarEvent[]): CalendarEvent[] => {
  const prioritized: CalendarEvent[] = [];

  // 1. First meeting/appointment
  const appointment = events.find(e => e.eventType === CalendarEventType.Appointment);
  if (appointment) prioritized.push(appointment);

  // 2. First committee
  const committee = events.find(e => e.eventType === CalendarEventType.Committee);
  if (committee) prioritized.push(committee);

  // 3. First high-priority reminder (if we have less than 3 events)
  if (prioritized.length < 3) {
    const highPriorityReminder = events.find(
      e => e.eventType === CalendarEventType.Reminder &&
           e.isHighPriority &&
           !prioritized.includes(e)
    );
    if (highPriorityReminder) prioritized.push(highPriorityReminder);
  }

  // 4. If we still have less than 3, fill with remaining meetings/committees
  if (prioritized.length < 3) {
    const remaining = events.filter(
      e => !prioritized.includes(e) &&
      (e.eventType === CalendarEventType.Appointment || e.eventType === CalendarEventType.Committee)
    );
    prioritized.push(...remaining.slice(0, 3 - prioritized.length));
  }

  // 5. If we still have less than 3, fill with any remaining events
  if (prioritized.length < 3) {
    const remaining = events.filter(e => !prioritized.includes(e));
    prioritized.push(...remaining.slice(0, 3 - prioritized.length));
  }

  return prioritized.slice(0, 3);
};

/**
 * Group events by date (returns Map<dateString, events[]>)
 */
export const groupEventsByDate = (events: CalendarEvent[]): Map<string, CalendarEvent[]> => {
  const grouped = new Map<string, CalendarEvent[]>();

  events.forEach(event => {
    // Extract date directly from ISO string to avoid timezone issues
    // "2025-11-01T10:00:00Z" -> "2025-11-01"
    const dateKey = event.startDateTime.split('T')[0];

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(event);
  });

  return grouped;
};

/**
 * Generate calendar grid (6 weeks x 7 days = 42 cells)
 * Returns array of dates including previous/next month padding
 */
export interface CalendarCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
  displayedEvents: CalendarEvent[]; // Max 3 prioritized events
  hasMore: boolean;
}

const formatDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Generate compact calendar grid for dashboard widget
 * Shows: last 3 days + current week + next week (~17-21 days total)
 */
export const generateCompactCalendarGrid = (
  eventsMap: Map<string, CalendarEvent[]>
): CalendarCell[] => {
  const cells: CalendarCell[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Start from 3 days ago
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 3);

  // End at 14 days from today (total: 3 past + 1 today + 14 future = 18 days)
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 14);

  // Get the start of the week for startDate (Sunday)
  const startOfWeek = new Date(startDate);
  const dayOfWeek = startOfWeek.getDay();
  startOfWeek.setDate(startDate.getDate() - dayOfWeek);

  // Generate cells from start of week to end date
  const currentDate = new Date(startOfWeek);
  const year = today.getFullYear();
  const month = today.getMonth();

  while (currentDate <= endDate) {
    const dateKey = formatDateKey(currentDate);
    const dayEvents = eventsMap.get(dateKey) || [];
    const displayedEvents = prioritizeEvents(dayEvents);

    const cellDate = new Date(currentDate);
    cells.push({
      date: cellDate,
      isCurrentMonth: cellDate.getMonth() === month && cellDate.getFullYear() === year,
      isToday: isToday(cellDate),
      events: dayEvents,
      displayedEvents,
      hasMore: dayEvents.length > 3
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return cells;
};

/**
 * Sort events by start time
 */
export const sortEventsByTime = (events: CalendarEvent[]): CalendarEvent[] => {
  return [...events].sort((a, b) => {
    return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();
  });
};

/**
 * Get event icon based on type
 */
export const getEventIcon = (event: CalendarEvent): React.ReactElement => {
  const iconSize = 12;
  switch (event.eventType) {
    case CalendarEventType.Appointment:
      return React.createElement(Calendar, { size: iconSize });
    case CalendarEventType.Committee:
      return React.createElement(Users, { size: iconSize });
    case CalendarEventType.Reminder:
      if (event.reminderType === 1) return React.createElement(Phone, { size: iconSize }); // Call
      if (event.reminderType === 5) return React.createElement(Wallet, { size: iconSize }); // Payment
      if (event.reminderType === 3) return React.createElement(FileText, { size: iconSize }); // Document
      return React.createElement(Bell, { size: iconSize }); // Default reminder
    default:
      return React.createElement(Pin, { size: iconSize });
  }
};

/**
 * Alias for formatEventTime (for convenience)
 */
export const formatTime = formatEventTime;

/**
 * Generate calendar grid for current week only (Sunday-Saturday)
 * Shows 7 cells total - one for each day of the week
 */
export const generateCurrentWeekGrid = (
  eventsMap: Map<string, CalendarEvent[]>
): CalendarCell[] => {
  const cells: CalendarCell[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get Sunday of current week
  const currentDay = today.getDay(); // 0 = Sunday
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - currentDay);

  // Generate 7 cells (Sunday through Saturday)
  for (let i = 0; i < 7; i++) {
    const date = new Date(sunday);
    date.setDate(sunday.getDate() + i);

    const dateKey = formatDateKey(date);
    const dayEvents = eventsMap.get(dateKey) || [];

    cells.push({
      date,
      isCurrentMonth: date.getMonth() === today.getMonth(),
      isToday: isToday(date),
      events: dayEvents,
      displayedEvents: dayEvents.slice(0, 3), // Max 3 events
      hasMore: dayEvents.length > 3
    });
  }

  return cells;
};

/**
 * Generate calendar grid for current week + next week (14 days total)
 * Shows 14 cells arranged in 2 rows of 7 days each
 * Row 1: Current week (Sunday-Saturday)
 * Row 2: Next week (Sunday-Saturday)
 * @param eventsMap - Map of date strings to events
 * @param weekOffset - Number of weeks to offset from current week (default 0)
 */
export const generateTwoWeeksGrid = (
  eventsMap: Map<string, CalendarEvent[]>,
  weekOffset: number = 0
): CalendarCell[] => {
  const cells: CalendarCell[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get Sunday of current week, then apply offset
  const currentDay = today.getDay(); // 0 = Sunday
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - currentDay + (weekOffset * 7));

  // Generate 14 cells (2 weeks: current + next)
  for (let i = 0; i < 14; i++) {
    const date = new Date(sunday);
    date.setDate(sunday.getDate() + i);

    const dateKey = formatDateKey(date);
    const dayEvents = eventsMap.get(dateKey) || [];

    cells.push({
      date,
      isCurrentMonth: date.getMonth() === today.getMonth(),
      isToday: isToday(date),
      events: dayEvents,
      displayedEvents: dayEvents.slice(0, 3), // Max 3 events
      hasMore: dayEvents.length > 3
    });
  }

  return cells;
};
